import { getOrCreateStoredId } from './eventIds';

const ATTRIBUTION_STORAGE_KEY = 'cxnft_funnel_attribution_v1';
const ATTRIBUTION_PARAM_PREFIXES = ['utm_', 'meta_'];
const ATTRIBUTION_PARAM_NAMES = [
  'fbclid',
  'z_campaign',
  'z_ad',
  'z_source',
  'funnel_variant',
  'variant',
];

function hasWindow() {
  return typeof window !== 'undefined';
}

function safeLocalStorage() {
  if (!hasWindow()) {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function readRawCookie(name) {
  if (!hasWindow() || !document.cookie) {
    return '';
  }

  const cookie = document.cookie
    .split(';')
    .map((row) => row.trim())
    .find((row) => row.startsWith(`${encodeURIComponent(name)}=`));

  return cookie ? cookie.split('=').slice(1).join('=') : '';
}

function writeRawCookie(name, value) {
  if (!hasWindow() || !value) {
    return;
  }

  const maxAgeSeconds = 90 * 24 * 60 * 60;
  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${encodeURIComponent(name)}=${value}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secureAttribute}`;
}

function decodeQueryKey(key) {
  try {
    return decodeURIComponent(key.replace(/\+/g, ' '));
  } catch (error) {
    return key;
  }
}

function readRawQueryParam(search, name) {
  const query = (search || '').replace(/^\?/, '');
  if (!query) {
    return '';
  }

  const normalizedName = name.toLowerCase();
  const entries = query.split('&');

  for (const entry of entries) {
    if (!entry) {
      continue;
    }

    const separatorIndex = entry.indexOf('=');
    const rawKey = separatorIndex === -1 ? entry : entry.slice(0, separatorIndex);
    const rawValue = separatorIndex === -1 ? '' : entry.slice(separatorIndex + 1);

    if (decodeQueryKey(rawKey).toLowerCase() === normalizedName && rawValue) {
      return rawValue;
    }
  }

  return '';
}

function parseAttributionParams(search) {
  const params = new URLSearchParams(search || '');
  const values = {};

  params.forEach((value, key) => {
    const normalizedKey = key.toLowerCase();
    const shouldCapture =
      ATTRIBUTION_PARAM_NAMES.includes(normalizedKey) ||
      ATTRIBUTION_PARAM_PREFIXES.some((prefix) => normalizedKey.startsWith(prefix));

    if (shouldCapture && value) {
      values[normalizedKey] = value;
    }
  });

  const rawFbclid = readRawQueryParam(search, 'fbclid');
  if (rawFbclid) {
    values.fbclid = rawFbclid;
  }

  return values;
}

function makeTouch({ params, variant, now, location }) {
  return {
    captured_at: now,
    route_path: location.pathname,
    page_url: location.href,
    referrer: document.referrer || '',
    funnel_variant: variant.key,
    z_source: params.z_source || 'direct',
    z_campaign: params.z_campaign || '',
    z_ad: params.z_ad || variant.zAd,
    params,
  };
}

function readStoredAttribution() {
  const storage = safeLocalStorage();
  if (!storage) {
    return null;
  }

  try {
    const stored = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
}

function writeStoredAttribution(value) {
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    // Attribution should never block rendering or CTA behavior.
  }
}

function createFbp() {
  return `fb.1.${Date.now()}.${Math.floor(Math.random() * 10 ** 10)}`;
}

function createFbc(fbclid) {
  if (!fbclid) {
    return '';
  }

  return `fb.1.${Date.now()}.${fbclid}`;
}

function isValidFbc(value) {
  return /^fb\.1\.\d+\.[^\s]+$/.test(value || '');
}

function fbcClickId(value) {
  if (!isValidFbc(value)) {
    return '';
  }

  return value.split('.').slice(3).join('.');
}

function resolveFbc({ rawCookieFbc, rawFbclid }) {
  if (rawFbclid) {
    if (isValidFbc(rawCookieFbc) && fbcClickId(rawCookieFbc) === rawFbclid) {
      return rawCookieFbc;
    }

    return createFbc(rawFbclid);
  }

  return isValidFbc(rawCookieFbc) ? rawCookieFbc : '';
}

export function captureAttribution({ variant }) {
  if (!hasWindow()) {
    return {
      visitor_session_id: getOrCreateStoredId('visitor_session_id', { session: true }),
      anonymous_user_id: getOrCreateStoredId('anonymous_user_id'),
      attribution: { first_touch: null, latest_touch: null },
      meta: {},
      params: {},
    };
  }

  const location = window.location;
  const params = parseAttributionParams(location.search);
  const now = new Date().toISOString();
  const latestTouch = makeTouch({ params, variant, now, location });
  const stored = readStoredAttribution();
  const attribution = {
    first_touch: stored?.first_touch || latestTouch,
    latest_touch: latestTouch,
  };

  writeStoredAttribution(attribution);

  const fbp = readRawCookie('_fbp') || createFbp();
  const rawCookieFbc = readRawCookie('_fbc');
  const fbc = resolveFbc({ rawCookieFbc, rawFbclid: params.fbclid });

  if (params.fbclid && fbc && fbc !== rawCookieFbc) {
    writeRawCookie('_fbc', fbc);
  }

  return {
    visitor_session_id: getOrCreateStoredId('visitor_session_id', { session: true }),
    anonymous_user_id: getOrCreateStoredId('anonymous_user_id'),
    attribution,
    params,
    meta: {
      fbclid: params.fbclid || '',
      fbp,
      fbc,
      meta_campaign_id: params.meta_campaign_id || '',
      meta_adset_id: params.meta_adset_id || '',
      meta_ad_id: params.meta_ad_id || '',
      meta_placement: params.meta_placement || '',
    },
  };
}

export function readAttributionSnapshot() {
  return readStoredAttribution();
}
